import { computed, Ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router/composables';
import type { Route } from 'vue-router';
import type { TCatalogFilter } from '@/domain/catalog/catalog-filters';
import {
  ICatalogQuery,
  parseCatalogQuery,
  serializeCatalogQuery,
  TRouteQuery,
} from '@/domain/catalog/catalog-query';
import { ignoreNavigationFailure } from '@/utils/navigation.utils';
import type {
  ICatalogEditorTarget,
  ICatalogPageState,
} from './catalog-page-state';

export interface ICatalogUrlDeps {
  /** The store's live menus other than the base one. */
  menuIds: Ref<number[]>;
  /** Until the menus load, a URL `mId` is trusted as it is. */
  menusLoaded: Ref<boolean>;
  /** The search text the list uses (debounced). */
  debouncedQuery: Ref<string>;
  /** `?cId=` without an editor: jump to that category (then it leaves the URL). */
  onCategoryJump: (categoryId: number) => void;
}

function sameEditorTarget(
  a: ICatalogEditorTarget | null,
  b: ICatalogEditorTarget | null
): boolean {
  if (!a || !b) return a === b;
  return (
    a.kind === b.kind &&
    a.id === b.id &&
    a.categoryId === b.categoryId &&
    a.prefillName === b.prefillName
  );
}

function sameQuery(a: Record<string, string>, b: Route['query']): boolean {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  return [...keys].every((key) => a[key] === b[key]);
}

/**
 * The Menú page's URL state (plan §5.5): menu, filter, search and editor
 * target live in the query. Opening an editor pushes (the back gesture
 * closes it); switching the target of an open docked editor, filters, menu
 * and search replace (search debounced).
 */
export function useCatalogUrl(state: ICatalogPageState, deps: ICatalogUrlDeps) {
  const route = useRoute();
  const router = useRouter();
  /** The open editor came from our own push: closing goes back. */
  let editorPushed = false;
  /** The name for a create opened from the search («Crear "{q}"»). */
  let pendingPrefill = '';

  function menuIdsFor(query: TRouteQuery): number[] {
    if (deps.menusLoaded.value) return deps.menuIds.value;
    // Not loaded yet: keep whatever the URL says.
    const raw = Number(Array.isArray(query.mId) ? query.mId[0] : query.mId);
    return Number.isInteger(raw) ? [raw] : [];
  }

  const parsed = computed<ICatalogQuery>(() =>
    parseCatalogQuery(route.query, { menuIds: menuIdsFor(route.query) })
  );

  function write(next: ICatalogQuery, mode: 'push' | 'replace') {
    const query = serializeCatalogQuery(next);
    if (sameQuery(query, route.query)) return;
    const location = { path: route.path, query };
    const navigation =
      mode === 'push' ? router.push(location) : router.replace(location);
    navigation.catch(ignoreNavigationFailure);
  }

  /** The URL as it should be for the current state (editor keys from the URL). */
  function current(): ICatalogQuery {
    return {
      mId: state.menuId,
      cId: parsed.value.item === 'new' ? parsed.value.cId : null,
      item: parsed.value.item,
      category: parsed.value.category,
      filter: state.filter,
      q: deps.debouncedQuery.value.trim(),
    };
  }

  // URL → state.
  watch(
    [parsed, deps.menusLoaded],
    () => {
      const query = parsed.value;
      state.menuId = query.mId;
      state.filter = query.filter;
      if (query.q !== deps.debouncedQuery.value.trim()) state.query = query.q;
      const editor: ICatalogEditorTarget | null =
        query.item !== null
          ? {
              kind: 'item',
              id: query.item,
              categoryId: query.item === 'new' ? query.cId : null,
              prefillName: query.item === 'new' ? pendingPrefill : '',
            }
          : query.category !== null
          ? {
              kind: 'category',
              id: query.category,
              categoryId: null,
              prefillName: '',
            }
          : null;
      // Same target: the same object (every card reads it; a search or
      // filter replace must not re-render them all).
      if (!sameEditorTarget(state.editor, editor)) state.editor = editor;
      if (!state.editor) {
        editorPushed = false;
        pendingPrefill = '';
      }

      if (query.cId !== null && query.item === null) {
        deps.onCategoryJump(query.cId);
        write({ ...query, cId: null }, 'replace');
      } else if (deps.menusLoaded.value) {
        // Drop invalid values (an unknown menu, a bad filter) from the URL.
        const clean = serializeCatalogQuery(query);
        if (!sameQuery(clean, route.query))
          router
            .replace({ path: route.path, query: clean })
            .catch(ignoreNavigationFailure);
      }
    },
    { immediate: true }
  );

  /**
   * The list under an open editor changed (filter, search, menu): the entry
   * before the editor shows the old list, so closing replaces instead of
   * going back to it.
   */
  function listChangedUnderEditor() {
    if (state.editor) editorPushed = false;
  }

  // Search → URL, debounced.
  watch(deps.debouncedQuery, (text) => {
    if (text.trim() === parsed.value.q) return;
    listChangedUnderEditor();
    write({ ...current(), q: text.trim() }, 'replace');
  });

  function setFilter(filter: TCatalogFilter) {
    state.pinnedIds = {};
    listChangedUnderEditor();
    write({ ...current(), filter }, 'replace');
  }

  function setMenu(menuId: number | null) {
    state.pinnedIds = {};
    write(
      { ...current(), mId: menuId, item: null, category: null, cId: null },
      'replace'
    );
  }

  /** A deep-linked editor of another menu: that menu, keeping the editor. */
  function showMenuOf(menuId: number | null) {
    state.pinnedIds = {};
    listChangedUnderEditor();
    write({ ...current(), mId: menuId }, 'replace');
  }

  function clearSearch() {
    state.query = '';
    state.pinnedIds = {};
    listChangedUnderEditor();
    write({ ...current(), q: '' }, 'replace');
  }

  /**
   * Every product of `menuId`: no filter and no search, in one URL write
   * (rows the assistant points at must be on screen). The editor stays.
   */
  function revealAll(menuId: number | null) {
    state.query = '';
    state.pinnedIds = {};
    listChangedUnderEditor();
    write({ ...current(), mId: menuId, filter: 'all', q: '' }, 'replace');
  }

  /** Opens the product editor; an open docked editor switches in place. */
  function openItem(itemId: number, docked: boolean) {
    const switching = !!state.editor && docked;
    write(
      { ...current(), item: itemId, category: null, cId: null },
      switching ? 'replace' : 'push'
    );
    if (!switching) editorPushed = true;
  }

  function openNewItem(categoryId: number | null, prefillName = '') {
    pendingPrefill = prefillName;
    const switching = !!state.editor;
    write(
      { ...current(), item: 'new', category: null, cId: categoryId },
      switching ? 'replace' : 'push'
    );
    if (!switching) editorPushed = true;
  }

  function openCategory(categoryId: number | 'new', docked: boolean) {
    const switching = !!state.editor && docked;
    write(
      { ...current(), item: null, category: categoryId, cId: null },
      switching ? 'replace' : 'push'
    );
    if (!switching) editorPushed = true;
  }

  /**
   * After a create, the new entity replaces `new` in the URL; a new product
   * without categories becomes a new category.
   */
  function replaceTarget(kind: 'item' | 'category', id: number | 'new') {
    write(
      {
        ...current(),
        item: kind === 'item' ? id : null,
        category: kind === 'category' ? id : null,
        cId: null,
      },
      'replace'
    );
  }

  /** Closes the editor: back to the entry we pushed, else a replace. */
  function closeEditor() {
    if (!state.editor) return;
    if (editorPushed) {
      editorPushed = false;
      router.back();
      return;
    }
    write({ ...current(), item: null, category: null, cId: null }, 'replace');
  }

  return {
    setFilter,
    setMenu,
    showMenuOf,
    clearSearch,
    revealAll,
    openItem,
    openNewItem,
    openCategory,
    replaceTarget,
    closeEditor,
  };
}

export type ICatalogUrl = ReturnType<typeof useCatalogUrl>;
